import React, { useEffect, useState } from 'react';
import { getSmartnodeLockedCoins } from '../services/apiService';
import { Card, Button, Table, Collapse, Spinner } from 'react-bootstrap';
import { FaLock, FaUnlockAlt, FaChartPie, FaDatabase, FaEye, FaEyeSlash, FaExternalLinkAlt } from 'react-icons/fa';
import useTitle from '../hooks/useTitle';
import { Bar, Pie } from 'react-chartjs-2';
import { Link } from 'react-router-dom';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend } from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend);

const SmartnodeLockedCoins = () => {
    useTitle("MENELIK - Locked Coins");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showKnown, setShowKnown] = useState(false);
    const [showUnknown, setShowUnknown] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const result = await getSmartnodeLockedCoins();
                setData(result);
            } catch (error) {
                console.error('Error fetching data:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    if (loading) {
        return <div className="text-center mt-5"><Spinner animation="border" size="lg" /></div>;
    }

    if (!data) {
        return <p className="text-center text-danger">❌ Error loading data.</p>;
    }

    const barChartData = {
        labels: ['Total Locked', 'Known Locked', 'Unknown Locked'],
        datasets: [
            {
                label: 'Smartnode Locked Coins',
                data: [
                    parseFloat(data.totalLockedCoins),
                    parseFloat(data.totalKnownLockedCoins),
                    parseFloat(data.totalUnknownLockedCoins)
                ],
                backgroundColor: ['#36A2EB', '#4BC0C0', '#FF6384']
            }
        ]
    };

    const pieChartData = {
        labels: ['Known', 'Unknown'],
        datasets: [
            {
                data: [parseFloat(data.totalKnownLockedCoins), parseFloat(data.totalUnknownLockedCoins)],
                backgroundColor: ['#4BC0C0', '#FF6384']
            }
        ]
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false
    };

    return (
        <div className="container mt-4">
            <h1 className="text-center fw-bold text-primary mb-4 d-flex align-items-center justify-content-center gap-3">
                <FaDatabase className="text-info fs-1 shadow-sm" />
                <span className="border-bottom border-3 border-primary pb-1 shadow-sm px-3 rounded">
                    Smartnode Locked Coins
                </span>
                <FaLock className="text-danger fs-1 shadow-sm" />
            </h1>


            <p className="text-center text-muted">
            This page provides detailed insights into Smartnode collateralized coins, displaying both known and unknown locked coins. 
            Smartnodes play a vital role in blockchain networks by providing additional security, processing transactions, and enabling 
            advanced blockchain functionalities. Below, you'll find an overview of total locked coins, graphs for distribution, 
            and a list of addresses involved in Smartnode collateral locking.
            </p>

            <Card className="mb-4 shadow-lg border-0 rounded-lg bg-light">
                <Card.Body>
                    <Card.Title className="text-primary">
                        <FaLock className="text-danger me-2" /> What are Smartnodes?
                    </Card.Title>
                    <Card.Text className="text-dark">
                    Smartnodes are full nodes that hold a minimum collateral amount to support the blockchain network. They provide services 
                    such as instant transactions, governance voting, and enhanced security features. The amount of locked collateral determines 
                    participation in Smartnode rewards.
                    </Card.Text>
                    <Link to="/smartnodes" className="btn btn-outline-primary fw-bold">
                        Learn More <FaExternalLinkAlt />
                    </Link>
                </Card.Body>
            </Card>

            <div className="row text-center">
                <div className="col-md-4">
                    <Card className="shadow-lg border-0 rounded-lg text-white bg-success">
                        <Card.Body>
                            <Card.Title><FaLock className="me-2" /> Total Locked</Card.Title>
                            <Card.Text className="fs-4 fw-bold">{data.totalLockedCoins} MENEL</Card.Text>
                        </Card.Body>
                    </Card>
                </div>
                <div className="col-md-4">
                    <Card className="shadow-lg border-0 rounded-lg text-white bg-primary">
                        <Card.Body>
                            <Card.Title><FaUnlockAlt className="me-2" /> Known Locked</Card.Title>
                            <Card.Text className="fs-4 fw-bold">{data.totalKnownLockedCoins} MENEL</Card.Text>
                        </Card.Body>
                    </Card>
                </div>
                <div className="col-md-4">
                    <Card className="shadow-lg border-0 rounded-lg text-white bg-danger">
                        <Card.Body>
                            <Card.Title><FaChartPie className="me-2" /> Unknown Locked</Card.Title>
                            <Card.Text className="fs-4 fw-bold">{data.totalUnknownLockedCoins} MENEL</Card.Text>
                        </Card.Body>
                    </Card>
                </div>
            </div>

            <div className="row mt-4">
                <div className="col-md-6">
                    <h4 className="text-center text-dark">
                        <FaChartPie className="text-warning me-2" /> Coin Distribution
                    </h4>
                    <div style={{ height: '300px' }} className="p-3 bg-white rounded shadow-lg">
                        <Bar data={barChartData} options={chartOptions} />
                    </div>
                </div>
                <div className="col-md-6">
                    <h4 className="text-center text-dark">
                        <FaLock className="text-success me-2" /> Known vs Unknown Collaterals
                    </h4>
                    <div style={{ height: '300px' }} className="p-3 bg-white rounded shadow-lg">
                        <Pie data={pieChartData} options={chartOptions} />
                    </div>
                </div>
            </div>

            <div className="mt-4">
                <Button 
                    variant="outline-primary" 
                    onClick={() => setShowKnown(!showKnown)} 
                    className="mb-2 fw-bold shadow-sm"
                >
                    {showKnown ? <FaEyeSlash /> : <FaEye />} {showKnown ? 'Hide' : 'Show'} Known Collaterals ({data.knownLockedAddressesCount})
                </Button>
                <Collapse in={showKnown}>
                    <div className="table-responsive">
                        <Table striped bordered hover className="shadow-lg bg-white">
                            <thead className="table-primary text-white">
                                <tr>
                                    <th>Address</th>
                                    <th>Balance (MENEL)</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.knownAddresses.map((addr, index) => (
                                    <tr key={index}>
                                        <td>
                                            <Link to={`/address/${addr.address}`} className="text-decoration-none text-primary">
                                                {addr.address} <FaExternalLinkAlt />
                                            </Link>
                                        </td>
                                        <td className="fw-bold">{addr.balance}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                </Collapse>
            </div>

            <div className="mt-4">
                <Button 
                    variant="outline-danger" 
                    onClick={() => setShowUnknown(!showUnknown)} 
                    className="mb-2 fw-bold shadow-sm"
                >
                    {showUnknown ? <FaEyeSlash /> : <FaEye />} {showUnknown ? 'Hide' : 'Show'} Unknown Collaterals ({data.unknownLockedAddressesCount})
                </Button>
                <Collapse in={showUnknown}>
                    <div className="table-responsive">
                        <Table striped bordered hover className="shadow-lg bg-white">
                            <thead className="table-danger text-white">
                                <tr>
                                    <th>Address</th>
                                    <th>Balance (MENEL)</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.unknownAddresses.map((addr, index) => (
                                    <tr key={index}>
                                        <td>
                                            <Link to={`/address/${addr.address}`} className="text-decoration-none text-danger">
                                                {addr.address} <FaExternalLinkAlt />
                                            </Link>
                                        </td>
                                        <td className="fw-bold">{addr.balance}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                </Collapse>
            </div>
        </div>
    );
};

export default SmartnodeLockedCoins;
